import { create } from 'zustand';
import type { Workflow } from '@n8n-builder/n8n-types';
import type { WorkflowSummary } from '@n8n-builder/api-client';
import { createApiClient } from '@n8n-builder/api-client';
import { API_BASE_URL } from '../lib/api-config';
import { CACHE_TTL, fetchWithCache } from '../utils/cache';

interface WorkflowState {
  workflows: WorkflowSummary[];
  currentWorkflow: Workflow | null;
  isLoading: boolean;
  workflowsLastFetchedAt: number | null;
  setWorkflows: (workflows: WorkflowSummary[]) => void;
  addWorkflow: (workflow: WorkflowSummary) => void;
  setCurrentWorkflow: (workflow: Workflow | null) => void;
  setLoading: (loading: boolean) => void;
  removeWorkflow: (id: string) => void;
  fetchWorkflows: (options?: { force?: boolean }) => void;
  invalidateWorkflows: () => void;
}

export const useWorkflowStore = create<WorkflowState>((set, get) => ({
  workflows: [],
  currentWorkflow: null,
  isLoading: false,
  workflowsLastFetchedAt: null,
  setWorkflows: (workflows) => set({ workflows, workflowsLastFetchedAt: Date.now() }),
  addWorkflow: (workflow) =>
    set((state) => ({ workflows: [workflow, ...state.workflows], workflowsLastFetchedAt: null })),
  setCurrentWorkflow: (workflow) => set({ currentWorkflow: workflow }),
  setLoading: (isLoading) => set({ isLoading }),
  removeWorkflow: (id) =>
    set((state) => ({
      workflows: state.workflows.filter((w) => w.id !== id),
      workflowsLastFetchedAt: null,
    })),
  fetchWorkflows: (options) => {
    const state = get();
    fetchWithCache(
      state.workflows.length > 0,
      state.workflowsLastFetchedAt,
      CACHE_TTL.workflows,
      async () => {
        // Only show loading spinner on first load (no cached data)
        if (state.workflows.length === 0) {
          set({ isLoading: true });
        }
        try {
          const apiClient = createApiClient(API_BASE_URL);
          const result = await apiClient.getWorkflows();
          if (result.data?.workflows) {
            set({ workflows: result.data.workflows, workflowsLastFetchedAt: Date.now() });
          }
        } catch (error) {
          console.error('Failed to fetch workflows:', error);
        } finally {
          set({ isLoading: false });
        }
      },
      options,
    );
  },
  invalidateWorkflows: () => set({ workflowsLastFetchedAt: null }),
}));
